import type {
  ReservationSlotTemplate,
  ReservationSlotTemplateInput,
  IntervalMinutes,
} from '../../domain/reservationSlots';
import type { ReservationSlotTemplateRepository } from '../../application/reservationSlots/ports/ReservationSlotTemplateRepository';
import { reservationSlotApi, type TemplateApiItem, type TemplateApiInput } from '../api/reservationSlotApi';

/**
 * APIレスポンス項目（snake_case）をドメイン型（camelCase）へ変換する
 */
function toDomain(item: TemplateApiItem): ReservationSlotTemplate {
  return {
    id: item.id,
    name: item.name,
    intervalMinutes: item.interval_minutes as IntervalMinutes,
    patternType: item.pattern_type,
    slotsPerInterval: item.slots_per_interval,
    slotsA: item.slots_a,
    slotsB: item.slots_b,
  };
}

/**
 * 入力データ（camelCase）をAPIリクエストボディ（snake_case）へ変換する
 */
function toApiInput(input: ReservationSlotTemplateInput): TemplateApiInput {
  return {
    name: input.name,
    interval_minutes: input.intervalMinutes,
    pattern_type: input.patternType,
    slots_per_interval: input.slotsPerInterval,
    slots_a: input.slotsA,
    slots_b: input.patternType === 'alternate' ? input.slotsB : null,
  };
}

/**
 * 解放枠テンプレート Repository 具象実装
 *
 * @description
 * ReservationSlotTemplateRepository インターフェースの具象実装。
 * API Clientを使用してバックエンドAPIと通信し、
 * snake_case から camelCase への変換を行う。
 *
 * @implements {ReservationSlotTemplateRepository}
 */
export class ReservationSlotTemplateRepositoryImpl implements ReservationSlotTemplateRepository {
  async findAll(): Promise<ReservationSlotTemplate[]> {
    const items = await reservationSlotApi.getTemplates();
    return items.map(toDomain);
  }

  async create(input: ReservationSlotTemplateInput): Promise<ReservationSlotTemplate> {
    const item = await reservationSlotApi.createTemplate(toApiInput(input));
    return toDomain(item);
  }

  async update(id: string, input: ReservationSlotTemplateInput): Promise<ReservationSlotTemplate> {
    const item = await reservationSlotApi.updateTemplate(id, toApiInput(input));
    return toDomain(item);
  }

  async delete(id: string): Promise<void> {
    await reservationSlotApi.deleteTemplate(id);
  }
}

export const reservationSlotTemplateRepository = new ReservationSlotTemplateRepositoryImpl();
